import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRef, useState } from "react";
import { NowLinkItems, NowLinkItemsAuth } from "../utils";
import { SubLayout } from "@/containers/layout/LandingLayout";
import { Props } from "../types";
import routes from "@/constants/routes";
import { useAuth } from "@/shared/hooks/useAuth";
import { useRentalApplication } from "@/shared/hooks/useRentalApplication";

const HeaderDesktop = ({ theme }: Props): JSX.Element => {
  const router = useRouter();
  const { isAuthenticated, isLoading: isLoadingUser } = useAuth({ redirectOnUnauth: false });
  const { rentalApplication, isLoading: isLoadingApp } = useRentalApplication({
    redirectOnUnauth: false,
  });
  const isLoading = isLoadingUser || isLoadingApp;

  const itemClassess = "relative group mr-9 mr-8 2xl:mr-12 px-2 pt-2 pb-3";

  const underlineClassess = (active: boolean) =>
    `absolute left-0 right-0 -bottom-0.5 h-[2px] bg-landing-orange origin-center transition-transform duration-300 ease-snappy ${
      active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
    }`;

  const renderAuthButton = () => {
    return rentalApplication?.status === "DRAFT" ? (
      <Link href={routes.rentalApplication.yourHome}>
        <a className="ml-4 bg-landing-cherry text-white font-bold px-4 py-2 rounded shadow">
          Register
        </a>
      </Link>
    ) : (
      <Link href={routes.dashboard.root}>
        <a className="ml-4 bg-landing-cherry text-white font-bold px-4 py-2 rounded shadow">
          Your Account
        </a>
      </Link>
    );
  };
  return (
    <nav
      className={`relative z-50 w-full text-white py-4 ${
        theme === "dark"
          ? "bg-landing-blue"
          : theme === "light"
          ? "bg-white"
          : theme === "lightBlue"
          ? "bg-[#F0F3FD]"
          : "bg-landing-ivory"
      }`}
    >
      <SubLayout allowOverflow>
        <div className="relative z-50 flex items-center justify-between w-full h-16">
          {/* Logo */}
          <Link href={routes.home}>
            <a className="relative w-32 h-8 xl:w-52 xl:h-16">
              <Image
                src={
                  theme === "dark"
                    ? "/shared/keyzy-horizontal.svg"
                    : "/shared/keyzy-horizontal-white.svg"
                }
                alt="keyzy logo"
                layout="fill"
                priority
              />
            </a>
          </Link>

          {/* Main Nav Links */}
          <ul className="relative flex flex-row items-center w-full xl:justify-end xl:flex-1">
            {(isAuthenticated ? NowLinkItemsAuth : NowLinkItems)?.map((item, idx) => {
              const isActive = router.pathname === item.href;

              return (
                <li key={idx} className={itemClassess}>
                  {!item.items ? (
                    /* Single link */
                    <Link href={item.href}>
                      <a className="relative flex flex-col mx-auto w-fit">
                        <span className={underlineClassess(isActive)} />
                        <span
                          className={`inline-block text-sm 3xl:text-base font-semibold pb-1 ${
                            theme === "dark" ? "text-white" : "text-landing-blue"
                          }`}
                        >
                          {item.caption}
                        </span>
                      </a>
                    </Link>
                  ) : (
                    /* Mega Menu Trigger */
                    <div className="relative group/main inline-flex">
                      <span className={underlineClassess(isActive)} />
                      <div className="flex items-center gap-2 cursor-pointer">
                        <span
                          className={`inline-block text-sm 3xl:text-base pb-1 font-semibold ${
                            theme === "dark" ? "text-white" : "text-landing-blue"
                          }`}
                        >
                          {item.caption}
                        </span>
                        <Image
                          src="/budget-calculator/chevron-orange-bottom.svg"
                          width={11}
                          height={8}
                          alt="down"
                          className="transition-transform duration-300 ease-snappy group-hover/main:rotate-180"
                        />
                      </div>

                      {/* Safe hover bridge */}
                      <div className="absolute left-0 top-full h-4 w-full" />

                      {/* Dropdown drawer */}
                      <div
                        className={`
                          absolute left-0 top-full mt-2 z-40
                          min-w-[280px] bg-white rounded-b-xl shadow-card-hover overflow-hidden
                          max-h-0 opacity-0 py-0 pointer-events-none
                          group-hover/main:max-h-[480px] group-hover/main:opacity-100 group-hover/main:py-2.5 group-hover/main:pointer-events-auto
                          transition-[max-height,opacity,padding] duration-300 ease-snappy
                        `}
                      >
                        <ul className="flex flex-col">
                          {item.items.map((child, j) => (
                            <li
                              key={j}
                              className="group/item"
                              style={{ transitionDelay: `${60 + j * 35}ms` }}
                            >
                              <Link href={child.href}>
                                <a
                                  className={`
                                    flex items-center justify-between gap-4 px-5 py-3
                                    opacity-0 -translate-y-1.5
                                    group-hover/main:opacity-100 group-hover/main:translate-y-0
                                    transition-all duration-200 ease-snappy
                                    hover:bg-landing-ivory
                                  `}
                                  style={{ transitionDelay: `${60 + j * 35}ms` }}
                                >
                                  <span
                                    className={`text-sm font-semibold text-landing-blue transition-colors duration-150 ${
                                      router.pathname === child.href
                                        ? "text-landing-orange"
                                        : "group-hover/item:text-landing-orange"
                                    }`}
                                  >
                                    {child.caption}
                                  </span>
                                  <span className="relative w-4 h-[7px] shrink-0 transition-transform duration-300 ease-snappy group-hover/item:translate-x-1">
                                    <Image
                                      src="/icons/arrow-right-grey-400.svg"
                                      alt=""
                                      layout="fill"
                                      className="transition-opacity duration-150 group-hover/item:opacity-0"
                                    />
                                    <Image
                                      src="/icons/hiw-right-arrow.svg"
                                      alt=""
                                      layout="fill"
                                      className="absolute inset-0 opacity-0 transition-opacity duration-150 group-hover/item:opacity-100"
                                    />
                                  </span>
                                </a>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}

            {/* Sign up */}
            {isLoading ? (
              <a className="ml-4 bg-landing-cherry text-white font-bold px-4 py-2 rounded shadow min-w-[128px] min-h-[40px] flex items-center justify-center">
                <Image src="/icons/dashboard-loading.svg" width={20} height={20} alt="loading" />
              </a>
            ) : isAuthenticated ? (
              renderAuthButton()
            ) : (
              <Link href={routes.register}>
                <a className="ml-4 bg-landing-cherry text-white font-bold px-4 text-center min-w-[128px] py-2 rounded shadow">
                  Sign up
                </a>
              </Link>
            )}

            {/* Search homes */}
            <Link href={routes.searchHomes}>
              <a className="ml-4 bg-landing-orange text-white font-bold px-4 py-2 rounded shadow">
                Search homes
              </a>
            </Link>
          </ul>
        </div>
      </SubLayout>
    </nav>
  );
};

export default HeaderDesktop;
